import { Link } from 'react-router-dom';

import type { RecentDailyRoutine } from '../../application/dto/DailyRoutineDetails';
import { addLogicalDays, dayOfWeek } from '../../domain/shared/LogicalDate';
import { useTranslation } from '../design-system/data/translations';

const WEEKDAY_KEYS = [
  'calendar.sun',
  'calendar.mon',
  'calendar.tue',
  'calendar.wed',
  'calendar.thu',
  'calendar.fri',
  'calendar.sat',
] as const;

function intensityBucket(percentage: number): 0 | 1 | 2 | 3 | 4 {
  if (percentage <= 0) return 0;
  if (percentage < 34) return 1;
  if (percentage < 67) return 2;
  if (percentage < 100) return 3;
  return 4;
}

function readableDate(logicalDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${logicalDate}T00:00:00.000Z`));
}

interface ActivityCalendarProps {
  readonly routines: readonly RecentDailyRoutine[];
}

export function ActivityCalendar({ routines }: ActivityCalendarProps) {
  const { t, locale } = useTranslation();
  if (routines.length === 0) return null;

  const byDate = new Map(routines.map((entry) => [entry.routine.logicalDate, entry]));
  const dates = [...byDate.keys()].toSorted();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  if (!firstDate || !lastDate) return null;

  const leadingGaps = dayOfWeek(firstDate);
  const cells: Array<{
    readonly date?: string | undefined;
    readonly entry?: RecentDailyRoutine | undefined;
  }> = [];
  for (let index = 0; index < leadingGaps; index += 1) cells.push({});

  let cursor = firstDate;
  while (cursor <= lastDate) {
    cells.push({ date: cursor, entry: byDate.get(cursor) });
    cursor = addLogicalDays(cursor, 1);
  }

  return (
    <div className="activity-calendar" role="group" aria-label={t('calendar.aria')}>
      <ul className="activity-calendar__weekdays" aria-hidden="true">
        {WEEKDAY_KEYS.map((key, index) => (
          <li key={index}>{t(key)}</li>
        ))}
      </ul>
      <ul className="activity-calendar__grid">
        {cells.map((cell, index) => {
          if (!cell.date) {
            return (
              <li
                key={`gap-${index}`}
                className="activity-calendar__cell activity-calendar__cell--empty"
                aria-hidden="true"
              />
            );
          }
          const dayNumber = Number(cell.date.slice(-2));
          if (!cell.entry) {
            return (
              <li
                key={cell.date}
                className="activity-calendar__cell activity-calendar__cell--unset"
              >
                <span aria-hidden="true">{dayNumber}</span>
              </li>
            );
          }
          const { routine, progress } = cell.entry;
          const bucket = intensityBucket(progress.percentage);
          return (
            <li key={cell.date}>
              <Link
                to={`/history/${routine.logicalDate}`}
                className={`activity-calendar__cell activity-calendar__cell--${bucket}`}
                aria-label={t('calendar.day_aria')
                  .replace('{date}', readableDate(routine.logicalDate, locale))
                  .replace('{percent}', String(progress.percentage))}
              >
                <span aria-hidden="true">{dayNumber}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <ul className="activity-calendar__legend" aria-hidden="true">
        <li>{t('calendar.less')}</li>
        {[0, 1, 2, 3, 4].map((bucket) => (
          <li
            key={bucket}
            className={`activity-calendar__cell activity-calendar__cell--${bucket}`}
          />
        ))}
        <li>{t('calendar.more')}</li>
      </ul>
    </div>
  );
}
